var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Component } from 'react';
import { ErrorBoundaryHelper } from '../../lib/errorHandler';
/**
 * A delightful error boundary that catches React errors and shows friendly messages
 */
var ErrorBoundary = /** @class */ (function (_super) {
    __extends(ErrorBoundary, _super);
    function ErrorBoundary(props) {
        var _this = _super.call(this, props) || this;
        _this.retryTimeoutId = null;
        _this.handleRetry = function () {
            _this.setState(function (prevState) { return ({
                hasError: false,
                error: undefined,
                retryCount: prevState.retryCount + 1
            }); });
        };
        _this.handleReload = function () {
            window.location.reload();
        };
        _this.state = {
            hasError: false,
            retryCount: 0
        };
        return _this;
    }
    ErrorBoundary.getDerivedStateFromError = function (error) {
        return {
            hasError: true,
            error: error,
            retryCount: 0
        };
    };
    ErrorBoundary.prototype.componentDidCatch = function (error, errorInfo) {
        // Log the error
        ErrorBoundaryHelper.logErrorInfo(error, errorInfo);
        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    };
    ErrorBoundary.prototype.render = function () {
        if (this.state.hasError) {
            // Custom fallback
            if (this.props.fallback) {
                return this.props.fallback;
            }
            // Default error UI with humor
            var retryCount = this.state.retryCount;
            var showGiveUp = retryCount >= 3;
            var funnyMessages = [
                "Oops! Something went a bit sideways 🎭",
                "Well, that's embarrassing... 😅",
                "Houston, we have a problem... 🚀",
                "The hamsters powering this fell asleep 🐹💤",
                "Error 404: Functionality not found 🔍"
            ];
            var encouragingMessages = [
                "Don't worry, it happens to the best of us!",
                "Even the best apps have bad hair days 💇‍♀️",
                "Technology is wonderful... when it works 🤖",
                "Time for a digital coffee break? ☕",
                "Maybe try turning it off and on again? 🔄"
            ];
            var randomFunnyMessage = funnyMessages[Math.floor(Math.random() * funnyMessages.length)];
            var randomEncouragingMessage = encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];
            return (_jsx("div", __assign({ className: "min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4" }, { children: _jsxs("div", __assign({ className: "max-w-md w-full bg-white dark:bg-muted rounded-xl shadow-xl border border-border dark:border-border overflow-hidden" }, { children: [_jsxs("div", __assign({ className: "bg-gradient-to-r from-red-500 to-pink-500 p-6 text-center" }, { children: [_jsx("div", __assign({ className: "text-6xl mb-3" }, { children: "\uD83E\uDD2F" })), _jsx("h1", __assign({ className: "text-xl font-bold text-white mb-2" }, { children: randomFunnyMessage })), _jsx("p", __assign({ className: "text-error text-sm" }, { children: randomEncouragingMessage }))] })), _jsxs("div", __assign({ className: "p-6" }, { children: [_jsx("div", __assign({ className: "text-center mb-6" }, { children: _jsx("p", __assign({ className: "text-muted-foreground dark:text-muted-foreground text-sm leading-relaxed" }, { children: "Something unexpected happened and our app got a bit confused. The good news? We're already working on making it smarter! \uD83E\uDDE0\u2728" })) })), process.env.NODE_ENV === 'development' && this.state.error && (_jsxs("details", __assign({ className: "mb-4 p-3 bg-muted dark:bg-muted rounded-lg text-xs" }, { children: [_jsx("summary", __assign({ className: "cursor-pointer text-foreground dark:text-muted-foreground font-medium" }, { children: "\uD83D\uDD27 Developer Details" })), _jsx("pre", __assign({ className: "mt-2 text-error dark:text-error whitespace-pre-wrap break-words" }, { children: this.state.error.stack }))] }))), _jsxs("div", __assign({ className: "space-y-3" }, { children: [!showGiveUp ? (_jsx("button", __assign({ onClick: this.handleRetry, className: "w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105" }, { children: retryCount === 0 ? "🔄 Let's Try Again!" :
                                                retryCount === 1 ? "🤞 One More Time!" :
                                                    retryCount === 2 ? "🍀 Third Time's the Charm!" :
                                                        "🙏 Please Work This Time!" }))) : (_jsxs("div", __assign({ className: "text-center" }, { children: [_jsx("p", __assign({ className: "text-sm text-muted-foreground dark:text-muted-foreground mb-3" }, { children: "Okay, maybe it's not you, it's us... \uD83D\uDE14" })), _jsx("button", __assign({ onClick: this.handleReload, className: "w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200" }, { children: "\uD83D\uDD04 Start Fresh" }))] }))), _jsx("button", __assign({ onClick: function () { return window.history.back(); }, className: "w-full bg-muted dark:bg-muted hover:bg-muted dark:hover:bg-muted text-foreground dark:text-muted-foreground font-medium py-3 px-4 rounded-lg transition-all duration-200" }, { children: "\u2B05\uFE0F Go Back" }))] })), _jsx("div", __assign({ className: "mt-6 pt-4 border-t border-border dark:border-border text-center" }, { children: _jsx("p", __assign({ className: "text-xs text-muted-foreground dark:text-muted-foreground" }, { children: "Still stuck? Our support team is here to help! \uD83D\uDCAA" })) }))] }))] })) })));
        }
        return this.props.children;
    };
    ErrorBoundary.prototype.componentWillUnmount = function () {
        if (this.retryTimeoutId) {
            clearTimeout(this.retryTimeoutId);
        }
    };
    return ErrorBoundary;
}(Component));
export { ErrorBoundary };
/**
 * Higher-order component to wrap components with error boundary
 */
export function withErrorBoundary(Component, fallback, onError) {
    var WrappedComponent = function (props) { return (_jsx(ErrorBoundary, __assign({ fallback: fallback, onError: onError }, { children: _jsx(Component, __assign({}, props)) }))); };
    WrappedComponent.displayName = "withErrorBoundary(".concat(Component.displayName || Component.name, ")");
    return WrappedComponent;
}
export default ErrorBoundary;
